import { Controller } from "@hotwired/stimulus"
import {
  CategoryScale,
  Chart,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip
} from "chart.js"

Chart.register(CategoryScale, Filler, LinearScale, LineController, LineElement, PointElement, Tooltip)

export default class extends Controller {
  static values = {
    data: Array,
    label: String,
    labels: Array,
    unit: String
  }

  connect() {
    this.renderChart()

    this.modal = this.element.closest(".modal")
    this.handleModalShown = () => this.chart?.resize()
    this.modal?.addEventListener("shown.bs.modal", this.handleModalShown)

    this.themeObserver = new MutationObserver(() => this.refreshColors())
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme"] })
  }

  disconnect() {
    this.modal?.removeEventListener("shown.bs.modal", this.handleModalShown)
    this.themeObserver?.disconnect()
    this.chart?.destroy()
  }

  renderChart() {
    const colors = this.colors()
    const singleValue = this.dataValue.length === 1

    this.chart = new Chart(this.element, {
      type: "line",
      data: {
        labels: this.labelsValue,
        datasets: [{
          label: this.labelValue,
          data: this.dataValue,
          borderColor: colors.line,
          backgroundColor: colors.area,
          borderWidth: 3,
          fill: true,
          pointBackgroundColor: colors.line,
          pointBorderColor: colors.background,
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          pointRadius: singleValue ? 6 : 3,
          tension: 0.28
        }]
      },
      options: {
        locale: "de-DE",
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            callbacks: {
              label: (context) => `${this.labelValue}: ${this.formattedValue(context.parsed.y)}`
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: colors.text, maxRotation: 0, maxTicksLimit: 7 }
          },
          y: {
            beginAtZero: false,
            grace: "8%",
            grid: { color: colors.grid },
            ticks: {
              color: colors.text,
              maxTicksLimit: 6,
              callback: (value) => this.formattedNumber(value)
            }
          }
        }
      }
    })
  }

  refreshColors() {
    if (!this.chart) return

    const colors = this.colors()
    const dataset = this.chart.data.datasets[0]

    dataset.borderColor = colors.line
    dataset.backgroundColor = colors.area
    dataset.pointBackgroundColor = colors.line
    dataset.pointBorderColor = colors.background
    this.chart.options.scales.x.ticks.color = colors.text
    this.chart.options.scales.y.ticks.color = colors.text
    this.chart.options.scales.y.grid.color = colors.grid
    this.chart.update("none")
  }

  formattedValue(value) {
    return [this.formattedNumber(value), this.unitValue].filter(Boolean).join(" ")
  }

  formattedNumber(value) {
    return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(value)
  }

  colors() {
    const styles = getComputedStyle(document.documentElement)
    const primaryRgb = styles.getPropertyValue("--bs-primary-rgb").trim()

    return {
      line: styles.getPropertyValue("--bs-primary").trim(),
      area: `rgba(${primaryRgb}, 0.14)`,
      background: styles.getPropertyValue("--bs-body-bg").trim(),
      text: styles.getPropertyValue("--bs-secondary-color").trim(),
      grid: styles.getPropertyValue("--bs-border-color").trim()
    }
  }
}
